/**
 * 🏢 ORGANIZATION SWITCHER - Clerk-Only Implementation
 * 
 * Dropdown component for switching between organizations using Clerk SDK
 * - Shows current organization
 * - Lists all user's organizations
 * - Allows switching between them
 * - Shows user's role in each org
 */

import { useOrganization } from "@/hooks/useOrganization";
import { Building2, Check, ChevronDown, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export function OrganizationSwitcher() {
  const {
    currentOrganization,
    organizations,
    switchOrganization,
    isLoading,
  } = useOrganization();

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrganization?.id) return;
    
    try {
      await switchOrganization(orgId);
      // Clerk will automatically update the JWT and trigger re-renders
    } catch (error) {
      console.error("Failed to switch organization:", error);
      // TODO: Show error toast
    }
  };

  if (isLoading || !currentOrganization) {
    return (
      <Button
        variant="outline"
        className="w-[200px] justify-between"
        disabled
      >
        <span className="flex items-center space-x-2">
          <Building2 className="w-4 h-4" />
          <span className="text-sm">Loading...</span>
        </span>
      </Button>
    );
  }

  // Get first two letters for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-[200px] justify-between"
        >
          <span className="flex items-center space-x-2 truncate">
            {currentOrganization.imageUrl ? (
              <Avatar className="w-4 h-4">
                <AvatarImage src={currentOrganization.imageUrl} alt={currentOrganization.name} />
                <AvatarFallback className="text-xs">
                  {getInitials(currentOrganization.name)}
                </AvatarFallback>
              </Avatar>
            ) : (
              <Building2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
            )}
            <span className="text-sm font-medium truncate">
              {currentOrganization.name}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-[280px]" align="start">
        <DropdownMenuLabel className="text-xs text-gray-500 uppercase font-semibold">
          Your Organizations
        </DropdownMenuLabel>
        
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => handleSwitch(org.id)}
            className="flex items-center justify-between cursor-pointer py-2"
          >
            <span className="flex items-center space-x-2 flex-1 min-w-0">
              {org.imageUrl ? (
                <Avatar className="w-6 h-6 flex-shrink-0">
                  <AvatarImage src={org.imageUrl} alt={org.name} />
                  <AvatarFallback className="text-xs">
                    {getInitials(org.name)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <Building2 className="w-5 h-5 text-gray-500 flex-shrink-0" />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium truncate">{org.name}</span>
                {org.role && (
                  <span className="text-xs text-gray-500 truncate">
                    {formatRole(org.role)}
                  </span>
                )}
              </div>
            </span>
            {currentOrganization.id === org.id && (
              <Check className="w-4 h-4 text-indigo-600 flex-shrink-0 ml-2" />
            )}
          </DropdownMenuItem>
        ))}

        {/* Option to use Clerk's built-in organization management */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            // You can use Clerk's <OrganizationSwitcher /> component instead
            // or navigate to Clerk's organization management page
            console.log("Manage organizations - integrate with Clerk");
          }}
          className="flex items-center space-x-2 text-gray-600"
        >
          <Users className="w-4 h-4" />
          <span className="text-sm">Manage Organizations</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Format Clerk role for display
function formatRole(role: string): string {
  switch (role) {
    case 'org:admin':
    case 'admin':
      return 'Admin';
    case 'owner':
      return 'Owner';
    case 'org:member':
    case 'member':
      return 'Member';
    default:
      return role;
  }
}
